import './styles.css';

const MAX_FILES = 8;
const MAX_FILE_BYTES = 20 * 1024 * 1024;
const MAX_PIXELS = 40 * 1000 * 1000;
const MAX_TOTAL_BYTES = 64 * 1024 * 1024;
const MAX_PREVIEW_EDGE = 1024;
const HEIF_BRANDS = new Set(['heic', 'heix', 'hevc', 'hevx', 'heif', 'heim', 'heis', 'hevm', 'hevs', 'mif1', 'msf1']);
const settledRun = Promise.resolve();

const elements = {
  fileInput: document.querySelector('#file-input'),
  dropZone: document.querySelector('#drop-zone'),
  queueList: document.querySelector('#queue-list'),
  queueCount: document.querySelector('#queue-count'),
  queueSummary: document.querySelector('#queue-summary'),
  convertButton: document.querySelector('#convert-button'),
  clearButton: document.querySelector('#clear-button'),
  resultPanel: document.querySelector('#result-panel'),
  resultSummary: document.querySelector('#result-summary'),
  zipButton: document.querySelector('#zip-button'),
  status: document.querySelector('#status-message'),
  qualityInput: document.querySelector('#quality-input'),
  qualityValue: document.querySelector('#quality-value'),
  qualityControl: document.querySelector('#quality-control'),
};

const state = {
  queue: [],
  format: 'jpg',
  quality: .88,
  runToken: 0,
  decoderPromise: null,
  activeWork: null,
  runBarrier: settledRun,
};

document.querySelectorAll('[data-year]').forEach((node) => { node.textContent = String(new Date().getFullYear()); });

function getStatusLabel(status) {
  return { queued: 'Ready', working: 'Converting…', success: 'Converted', error: 'Could not convert' }[status] || status;
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function setStatus(message, kind = '') {
  elements.status.textContent = message;
  elements.status.className = `status-message${kind ? ` ${kind}` : ''}`;
}

function invalidateRun() {
  state.runToken += 1;
  if (state.activeWork) state.runBarrier = state.activeWork.catch(() => {});
}

async function waitForRunBarrier() {
  const barrier = state.runBarrier;
  if (barrier === settledRun) return;
  setStatus('Finishing the previous conversion before starting another one…');
  await barrier;
  if (state.runBarrier === barrier) state.runBarrier = settledRun;
}

function isHeifSignature(bytes) {
  if (bytes.length < 12) return false;
  const text = (start, length) => String.fromCharCode(...bytes.slice(start, start + length));
  if (text(4, 4) !== 'ftyp') return false;
  for (let offset = 8; offset + 4 <= Math.min(bytes.length, 64); offset += 4) {
    if (HEIF_BRANDS.has(text(offset, 4))) return true;
  }
  return false;
}

async function validateFile(file) {
  if (!file || file.size === 0) throw new Error('The file is empty.');
  if (file.size > MAX_FILE_BYTES) throw new Error(`The file is larger than ${MAX_FILE_BYTES / (1024 * 1024)} MB.`);
  const header = new Uint8Array(await file.slice(0, 64).arrayBuffer());
  if (!isHeifSignature(header)) throw new Error('This does not look like a supported HEIC or HEIF file.');
}

function nextId() {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function revokeEntry(entry) {
  if (entry.outputUrl) URL.revokeObjectURL(entry.outputUrl);
  if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
  entry.outputUrl = null;
  entry.previewUrl = null;
  entry.outputBlob = null;
}

function totalBytes() {
  return state.queue.reduce((sum, item) => sum + item.file.size, 0);
}

async function addFiles(fileList) {
  const incoming = [...fileList];
  if (!incoming.length) return;
  if (state.queue.length + incoming.length > MAX_FILES) {
    setStatus(`A batch can contain up to ${MAX_FILES} files.`, 'error');
    return;
  }
  if (totalBytes() + incoming.reduce((sum, file) => sum + file.size, 0) > MAX_TOTAL_BYTES) {
    setStatus(`Keep the total batch below ${MAX_TOTAL_BYTES / (1024 * 1024)} MB.`, 'error');
    return;
  }
  await waitForRunBarrier();
  if (state.queue.length + incoming.length > MAX_FILES) {
    setStatus(`A batch can contain up to ${MAX_FILES} files.`, 'error');
    return;
  }
  if (totalBytes() + incoming.reduce((sum, file) => sum + file.size, 0) > MAX_TOTAL_BYTES) {
    setStatus(`Keep the total batch below ${MAX_TOTAL_BYTES / (1024 * 1024)} MB.`, 'error');
    return;
  }
  for (const file of incoming) {
    const entry = { id: nextId(), file, status: 'queued', error: '', outputBlob: null, outputUrl: null, previewUrl: null, width: 0, height: 0 };
    try {
      await validateFile(file);
      state.queue.push(entry);
    } catch (error) {
      entry.status = 'error';
      entry.error = error instanceof Error ? error.message : 'The file could not be checked.';
      state.queue.push(entry);
    }
  }
  renderQueue();
  if (state.queue.some((entry) => entry.status === 'queued')) setStatus('Files are ready. Choose Convert all when you are ready.');
  elements.fileInput.value = '';
}

function buildQueueItem(entry) {
  const item = document.createElement('article');
  item.className = 'queue-item';
  item.dataset.id = entry.id;
  const thumb = document.createElement('div');
  thumb.className = 'queue-thumb';
  if (entry.previewUrl) {
    const image = document.createElement('img');
    image.src = entry.previewUrl;
    image.alt = `Preview of ${entry.file.name}`;
    thumb.append(image);
  } else {
    thumb.textContent = entry.file.name.toLowerCase().endsWith('.heif') ? 'HEIF' : 'HEIC';
  }
  const details = document.createElement('div');
  details.className = 'queue-details';
  const name = document.createElement('div');
  name.className = 'queue-name';
  name.title = entry.file.name;
  name.textContent = entry.file.name;
  const meta = document.createElement('div');
  meta.className = 'queue-meta';
  meta.textContent = entry.width ? `${formatBytes(entry.file.size)} · ${entry.width} × ${entry.height}` : formatBytes(entry.file.size);
  const status = document.createElement('div');
  status.className = `queue-status ${entry.status}`;
  status.textContent = entry.status === 'error' ? entry.error : getStatusLabel(entry.status);
  details.append(name, meta, status);
  const actions = document.createElement('div');
  actions.className = 'queue-actions';
  if (entry.status === 'success') {
    const download = document.createElement('button');
    download.className = 'mini-button';
    download.type = 'button';
    download.textContent = 'Download';
    download.addEventListener('click', () => downloadEntry(entry));
    actions.append(download);
  }
  if (entry.status === 'error') {
    const retry = document.createElement('button');
    retry.className = 'mini-button';
    retry.type = 'button';
    retry.textContent = 'Retry';
    retry.addEventListener('click', () => {
      entry.status = 'queued';
      entry.error = '';
      renderQueue();
      convertAll();
    });
    actions.append(retry);
  }
  const remove = document.createElement('button');
  remove.className = 'mini-button';
  remove.type = 'button';
  remove.textContent = 'Remove';
  remove.setAttribute('aria-label', `Remove ${entry.file.name}`);
  remove.addEventListener('click', () => removeEntry(entry.id));
  actions.append(remove);
  item.append(thumb, details, actions);
  return item;
}

function renderQueue() {
  elements.queueList.replaceChildren(...state.queue.map(buildQueueItem));
  const ready = state.queue.filter((entry) => entry.status === 'queued').length;
  const working = state.queue.filter((entry) => entry.status === 'working').length;
  const success = state.queue.filter((entry) => entry.status === 'success').length;
  const failed = state.queue.filter((entry) => entry.status === 'error').length;
  elements.queueCount.textContent = String(state.queue.length);
  elements.queueSummary.textContent = state.queue.length === 0 ? 'Add a file to begin.' : `${ready} ready · ${working} working · ${success} complete${failed ? ` · ${failed} failed` : ''}`;
  elements.convertButton.disabled = !state.queue.some((entry) => entry.status === 'queued') || working > 0;
  elements.clearButton.disabled = state.queue.length === 0;
  const successes = state.queue.filter((entry) => entry.status === 'success' && entry.outputBlob);
  elements.resultPanel.hidden = successes.length === 0;
  elements.resultSummary.textContent = successes.length ? `${successes.length} ${successes.length === 1 ? 'file is' : 'files are'} ready as ${state.format.toUpperCase()}.` : '';
}

function removeEntry(id) {
  invalidateRun();
  const index = state.queue.findIndex((entry) => entry.id === id);
  if (index < 0) return;
  revokeEntry(state.queue[index]);
  state.queue.splice(index, 1);
  renderQueue();
  setStatus('File removed from the queue.');
}

function clearQueue() {
  const wasWorking = Boolean(state.activeWork);
  invalidateRun();
  state.queue.forEach(revokeEntry);
  state.queue = [];
  renderQueue();
  setStatus(wasWorking
    ? 'Queue cleared. Finishing the previous conversion before new files can start.'
    : 'Queue cleared. Your originals were never uploaded.');
}

async function loadDecoder() {
  if (!state.decoderPromise) {
    setStatus('Loading the local HEIC decoder…');
    state.decoderPromise = import('./vendor/libheif-bundle.mjs').then(({ default: factory }) => factory());
  }
  return state.decoderPromise;
}

function canvasBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('The browser could not create the output image.')), type, quality);
  });
}

async function decodeEntry(entry) {
  const libheif = await loadDecoder();
  const decoder = new libheif.HeifDecoder();
  const bytes = new Uint8Array(await entry.file.arrayBuffer());
  const images = decoder.decode(bytes);
  if (!images?.length) throw new Error('No image frame was found in this file.');
  const image = images[0];
  const width = image.get_width();
  const height = image.get_height();
  if (!width || !height || width * height > MAX_PIXELS) {
    images.forEach((decoded) => decoded.free?.());
    throw new Error(`The decoded image is larger than ${MAX_PIXELS / 1000000} megapixels.`);
  }
  entry.width = width;
  entry.height = height;
  let imageData = new ImageData(width, height);
  let outputCanvas = null;
  try {
    await new Promise((resolve, reject) => {
      image.display(imageData, (displayData) => displayData ? resolve() : reject(new Error('The HEIC pixels could not be decoded.')));
    });
    outputCanvas = document.createElement('canvas');
    outputCanvas.width = width;
    outputCanvas.height = height;
    const context = outputCanvas.getContext('2d', { alpha: true });
    context.putImageData(imageData, 0, 0);
    if (state.format === 'jpg') {
      context.globalCompositeOperation = 'destination-over';
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, width, height);
      context.globalCompositeOperation = 'source-over';
    }
    const mime = state.format === 'jpg' ? 'image/jpeg' : 'image/png';
    return await canvasBlob(outputCanvas, mime, state.format === 'jpg' ? state.quality : undefined);
  } finally {
    if (outputCanvas) {
      outputCanvas.width = 0;
      outputCanvas.height = 0;
    }
    imageData = null;
    images.forEach((decoded) => decoded.free?.());
  }
}

async function createPreviewUrl(blob, width, height) {
  if (Math.max(width, height) <= MAX_PREVIEW_EDGE || typeof createImageBitmap !== 'function') {
    return URL.createObjectURL(blob);
  }
  const scale = MAX_PREVIEW_EDGE / Math.max(width, height);
  const previewWidth = Math.max(1, Math.round(width * scale));
  const previewHeight = Math.max(1, Math.round(height * scale));
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = previewWidth;
  canvas.height = previewHeight;
  try {
    canvas.getContext('2d', { alpha: true }).drawImage(bitmap, 0, 0, previewWidth, previewHeight);
    const previewBlob = await canvasBlob(canvas, blob.type, state.format === 'jpg' ? state.quality : undefined);
    return URL.createObjectURL(previewBlob);
  } finally {
    bitmap.close();
    canvas.width = 0;
    canvas.height = 0;
  }
}

async function convertEntry(entry, token) {
  if (token !== state.runToken || !state.queue.includes(entry)) return;
  entry.status = 'working';
  entry.error = '';
  renderQueue();
  try {
    const blob = await decodeEntry(entry);
    if (token !== state.runToken || !state.queue.includes(entry)) return;
    revokeEntry(entry);
    const previewUrl = await createPreviewUrl(blob, entry.width, entry.height);
    if (token !== state.runToken || !state.queue.includes(entry)) {
      URL.revokeObjectURL(previewUrl);
      return;
    }
    entry.outputBlob = blob;
    entry.previewUrl = previewUrl;
    entry.status = 'success';
  } catch (error) {
    if (token !== state.runToken || !state.queue.includes(entry)) return;
    entry.status = 'error';
    entry.error = error instanceof Error ? error.message : 'The file could not be converted.';
  }
  renderQueue();
}

async function convertAll() {
  const requestedToken = state.runToken;
  const previousWork = state.activeWork;
  if (previousWork) await previousWork.catch(() => {});
  await waitForRunBarrier();
  if (requestedToken !== state.runToken) return;
  const pending = state.queue.filter((entry) => entry.status === 'queued');
  if (!pending.length) return;
  const token = ++state.runToken;
  setStatus('Converting locally. You can keep this tab open while the queue runs.');
  let cursor = 0;
  const worker = async () => {
    while (cursor < pending.length) {
      const entry = pending[cursor];
      cursor += 1;
      await convertEntry(entry, token);
    }
  };
  const work = Promise.all([worker(), worker()]);
  state.activeWork = work;
  try {
    await work;
    if (token === state.runToken) {
      const succeeded = state.queue.filter((entry) => entry.status === 'success').length;
      const failed = state.queue.filter((entry) => entry.status === 'error').length;
      setStatus(failed ? `${succeeded} converted; ${failed} need attention.` : `${succeeded} ${succeeded === 1 ? 'file is' : 'files are'} ready to download.`);
    }
  } finally {
    if (state.activeWork === work) state.activeWork = null;
  }
  renderQueue();
}

function outputName(entry) {
  return entry.file.name.replace(/\.(heic|heif)$/i, '') + (state.format === 'jpg' ? '.jpg' : '.png');
}

function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function downloadEntry(entry) {
  if (entry.outputBlob) downloadBlob(entry.outputBlob, outputName(entry));
}

async function downloadZip() {
  const successes = state.queue.filter((entry) => entry.status === 'success' && entry.outputBlob);
  if (!successes.length) return;
  elements.zipButton.disabled = true;
  setStatus('Building the ZIP locally…');
  try {
    const { default: JSZip } = await import('jszip');
    const zip = new JSZip();
    successes.forEach((entry) => zip.file(outputName(entry), entry.outputBlob));
    const blob = await zip.generateAsync({ type: 'blob', compression: 'STORE' });
    downloadBlob(blob, `kivoza-converted-${state.format}.zip`);
    setStatus('ZIP downloaded. It was created in this browser tab.');
  } catch (error) {
    setStatus(error instanceof Error ? error.message : 'The ZIP could not be created.', 'error');
  } finally {
    elements.zipButton.disabled = false;
  }
}

function resetForSettingChange() {
  invalidateRun();
  state.queue.forEach((entry) => {
    if (entry.status === 'error' && !entry.width && !entry.height) return;
    revokeEntry(entry);
    if (entry.status === 'success' || entry.status === 'error' || entry.status === 'working') entry.status = 'queued';
    entry.error = '';
    entry.width = 0;
    entry.height = 0;
  });
  renderQueue();
  if (state.queue.some((entry) => entry.status === 'error')) setStatus('Some files need attention.', 'error');
  else if (state.queue.length) setStatus('Output settings changed. Convert the queue again to create new files.');
}

elements.fileInput.addEventListener('change', (event) => addFiles(event.target.files));
elements.dropZone.addEventListener('click', () => elements.fileInput.click());
elements.dropZone.addEventListener('keydown', (event) => {
  if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); elements.fileInput.click(); }
});
['dragenter', 'dragover'].forEach((eventName) => elements.dropZone.addEventListener(eventName, (event) => {
  event.preventDefault();
  elements.dropZone.classList.add('is-dragging');
}));
['dragleave', 'drop'].forEach((eventName) => elements.dropZone.addEventListener(eventName, (event) => {
  event.preventDefault();
  elements.dropZone.classList.remove('is-dragging');
}));
elements.dropZone.addEventListener('drop', (event) => addFiles(event.dataTransfer.files));
elements.convertButton.addEventListener('click', convertAll);
elements.clearButton.addEventListener('click', clearQueue);
elements.zipButton.addEventListener('click', downloadZip);
elements.qualityInput.addEventListener('input', () => {
  state.quality = Number(elements.qualityInput.value) / 100;
  elements.qualityValue.value = elements.qualityInput.value;
  elements.qualityValue.textContent = elements.qualityInput.value;
});
elements.qualityInput.addEventListener('change', resetForSettingChange);
elements.qualityControl.hidden = state.format === 'png';
document.querySelectorAll('input[name="output-format"]').forEach((input) => input.addEventListener('change', () => {
  state.format = document.querySelector('input[name="output-format"]:checked')?.value || 'jpg';
  elements.qualityControl.hidden = state.format === 'png';
  resetForSettingChange();
}));

renderQueue();
